import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Crown, MapPin, Phone, Target, Map, Layers } from 'lucide-react'
import clsx from 'clsx'
import axios from 'axios'
import { MapContainer, TileLayer, GeoJSON, CircleMarker, Tooltip } from 'react-leaflet'
import type { Layer, LeafletMouseEvent, LatLngExpression } from 'leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { buildApiUrl } from '../../../config/api'
import { Card, SlidePanel, EmptyState } from '../../../components/ui'
import { formatPhone } from '../../../utils/masks'

interface CityData {
  city_id: number
  city_name: string
  city_ibge_id: number
  state_code: string
  state_id: number
  state_ibge_id: number
  leaders_count: number
  vote_goal_sum?: number
}

interface CityLeader {
  id: number
  name: string
  nickname: string | null
  phone: string | null
  neighborhood: string | null
  neighborhood_name: string | null
}

interface ScopeData {
  scope: string
  state_id?: number
  state_ibge_id?: number
  state_code?: string
  state_name?: string
  city_id?: number
  city_ibge_id?: number
  city_name?: string
}

interface LeaderPin {
  id: number
  name: string
  nickname: string | null
  latitude: number
  longitude: number
}

interface MapData {
  cities: CityData[]
  total_leaders: number
  leaders_with_area: number
  leaders_without_area: number
  has_campaign: boolean
  total_vote_goal: number
  scope: ScopeData | null
  leader_pins: LeaderPin[]
}

type RankingView = 'leaders' | 'vote_goal'

export default function LeadersMapPage() {
  const [data, setData] = useState<MapData | null>(null)
  const [filter, setFilter] = useState<'all' | 'campaign' | 'not_campaign'>('campaign')
  const [loading, setLoading] = useState(true)
  const [geoJson, setGeoJson] = useState<any>(null)
  const [geoLoading, setGeoLoading] = useState(false)
  const [cityNames, setCityNames] = useState<Record<string, string>>({})
  const [focusCityIbgeId, setFocusCityIbgeId] = useState<number | null>(null)
  const [selectedCity, setSelectedCity] = useState<CityData | null>(null)
  const [cityLeaders, setCityLeaders] = useState<CityLeader[]>([])
  const [cityLoading, setCityLoading] = useState(false)
  const mapFitted = useRef(false)
  const [rankingView, setRankingView] = useState<RankingView>('leaders')
  const [mapView, setMapView] = useState<'heat' | 'pins'>('heat')
  const navigate = useNavigate()

  useEffect(() => {
    if (filter !== 'campaign') setRankingView('leaders')
    fetchMap()
  }, [filter])

  async function fetchMap() {
    setLoading(true)
    mapFitted.current = false
    try {
      const res = await axios.get(buildApiUrl('/v1/reports/leaders/map'), { params: { filter } })
      setData(res.data.data)
      const scopeInfo = res.data.data.scope
      setFocusCityIbgeId(scopeInfo?.scope === 'MUNICIPAL' && scopeInfo?.city_ibge_id ? scopeInfo.city_ibge_id : null)
      if (scopeInfo?.state_id) {
        await loadGeoJson(scopeInfo.state_id)
      }
    } catch { /* */ } finally { setLoading(false) }
  }

  async function loadGeoJson(stateId: number) {
    setGeoLoading(true)
    try {
      const [geoRes, citiesRes] = await Promise.all([
        axios.get(buildApiUrl(`/v1/geo/states/${stateId}/boundaries`)),
        axios.get(buildApiUrl(`/v1/geo/states/${stateId}/cities`)),
      ])
      setGeoJson(geoRes.data)
      const names: Record<string, string> = {}
      citiesRes.data.data.forEach((c: any) => { names[String(c.ibge_id)] = c.name })
      setCityNames(names)
    } catch (err) { console.error('Erro ao carregar GeoJSON:', err) } finally { setGeoLoading(false) }
  }

  async function openCity(city: CityData) {
    setSelectedCity(city)
    setCityLoading(true)
    try {
      const res = await axios.get(buildApiUrl(`/v1/reports/leaders/city/${city.city_id}`))
      setCityLeaders(res.data.data.leaders)
    } catch { /* */ } finally { setCityLoading(false) }
  }

  const leadersByIbge: Record<number, CityData> = {}
  data?.cities?.forEach(c => { leadersByIbge[c.city_ibge_id] = c })
  const maxCount = data?.cities ? Math.max(...data.cities.map(c => c.leaders_count), 1) : 1

  const getCityValue = useCallback((city: CityData) => {
    return rankingView === 'vote_goal' ? (city.vote_goal_sum || 0) : city.leaders_count
  }, [rankingView])

  const rankedCities = useMemo(() => {
    if (!data?.cities) return []
    return [...data.cities].sort((a, b) => getCityValue(b) - getCityValue(a))
  }, [data, rankingView, getCityValue])

  const maxRankingCount = useMemo(() => {
    if (!rankedCities.length) return 1
    return Math.max(...rankedCities.map(c => getCityValue(c)), 1)
  }, [rankedCities, getCityValue])

  function getColor(count: number): string {
    if (count === 0) return '#e4e7ec'
    const ratio = count / maxCount
    if (ratio >= 0.8) return '#2a31d8'
    if (ratio >= 0.6) return '#465fff'
    if (ratio >= 0.4) return '#6b82fa'
    if (ratio >= 0.2) return '#93aafd'
    return '#bfcefe'
  }

  const styleFeature = useCallback((feature: any) => {
    const ibgeId = feature?.properties?.codarea
    const cityData = ibgeId ? leadersByIbge[Number(ibgeId)] : null
    const isFocusCity = focusCityIbgeId && Number(ibgeId) === focusCityIbgeId

    if (mapView === 'pins') {
      return {
        fillColor: 'transparent',
        weight: isFocusCity ? 2 : 0.5,
        opacity: isFocusCity ? 1 : 0.3,
        color: isFocusCity ? '#465fff' : '#9ca3af',
        fillOpacity: 0,
      }
    }

    return {
      fillColor: isFocusCity ? '#dbeafe' : getColor(cityData?.leaders_count || 0),
      weight: isFocusCity ? 3 : 1,
      opacity: 1,
      color: isFocusCity ? '#465fff' : 'white',
      fillOpacity: isFocusCity ? 0.4 : 0.85,
    }
  }, [data, focusCityIbgeId, mapView])

  const onEachFeature = useCallback((feature: any, layer: Layer) => {
    const ibgeId = feature?.properties?.codarea
    const cityData = ibgeId ? leadersByIbge[Number(ibgeId)] : null
    const name = (ibgeId && cityNames[String(ibgeId)]) || feature?.properties?.nome || 'Desconhecido'
    const count = cityData?.leaders_count || 0

    layer.bindTooltip(`<strong>${name}</strong><br/>${count} liderança${count !== 1 ? 's' : ''}`, { sticky: true })

    layer.on({
      mouseover: (e: LeafletMouseEvent) => {
        e.target.setStyle({ weight: 2, color: '#465fff', fillOpacity: 0.95 })
      },
      mouseout: (e: LeafletMouseEvent) => {
        e.target.setStyle({ weight: 1, color: 'white', fillOpacity: 0.85 })
      },
      click: () => {
        if (cityData) openCity(cityData)
      },
    })
  }, [data, cityNames])

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/reports/map')} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-gray-200/60 text-gray-400 hover:text-gray-600 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Mapa de Lideranças</h1>
            <p className="text-gray-500 mt-1">Distribuição geográfica por município</p>
          </div>
        </div>
        {data?.has_campaign && (
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {(['all', 'campaign', 'not_campaign'] as const).map(f => (
              <button key={f} onClick={() => setFilter(f)} className={clsx('px-3 py-1.5 rounded-md text-sm font-medium transition-colors', filter === f ? 'bg-brand-50 text-brand-600 shadow-xs' : 'text-gray-500')}>
                {f === 'all' ? 'Todos' : f === 'campaign' ? 'Campanha' : 'Fora'}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <Card>
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-brand-200 border-t-brand-500 rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm text-gray-500">Carregando...</p>
            </div>
          </div>
        </Card>
      ) : data && (!data.scope || (data.scope.scope !== 'NATIONAL' && !data.scope.state_ibge_id)) ? (
        <Card>
          <EmptyState
            icon={<MapPin className="w-12 h-12" />}
            title="Abrangência incompleta"
            description="Configure a abrangência e selecione o estado/município em Configurações > Político para visualizar o mapa."
          />
        </Card>
      ) : data ? (<>
        {/* Stats */}
        {data && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { v: data.total_leaders, l: 'Lideranças' },
              { v: data.cities.length, l: 'Cidades' },
              { v: data.leaders_without_area, l: 'Sem área', warn: true },
              ...(filter === 'campaign' && data.total_vote_goal > 0 ? [{ v: data.total_vote_goal.toLocaleString('pt-BR'), l: 'Meta de votos' }] : []),
            ].map(s => (
              <div key={s.l} className="bg-white rounded-2xl border border-gray-200 p-4 text-center">
                <p className={clsx('text-2xl font-semibold', s.warn ? 'text-warning-500' : 'text-gray-900')}>{s.v}</p>
                <p className="text-xs text-gray-500 mt-1">{s.l}</p>
              </div>
            ))}
          </div>
        )}

        {/* Mapa */}
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button onClick={() => setMapView('heat')} className={clsx('px-3 py-1 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5', mapView === 'heat' ? 'bg-white text-gray-800 shadow-xs' : 'text-gray-500')}>
              <Layers className="w-3.5 h-3.5" />Calor
            </button>
            <button onClick={() => setMapView('pins')} className={clsx('px-3 py-1 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5', mapView === 'pins' ? 'bg-white text-gray-800 shadow-xs' : 'text-gray-500')}>
              <Map className="w-3.5 h-3.5" />Mapa
            </button>
          </div>
        </div>
        <Card padding="none">
          <div style={{ height: '500px' }} className="rounded-2xl overflow-hidden">
            {geoLoading ? (
              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-8 h-8 border-4 border-brand-200 border-t-brand-500 rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-sm text-gray-500">Carregando mapa...</p>
                </div>
              </div>
            ) : geoJson ? (
              <MapContainer center={[-14.24, -51.93]} zoom={4} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true} ref={mapRef => {
                if (mapRef && geoJson && !mapFitted.current) {
                  mapFitted.current = true
                  setTimeout(() => {
                    try {
                      if (focusCityIbgeId && geoJson.features) {
                        const cityFeature = geoJson.features.find((f: any) => Number(f.properties?.codarea) === focusCityIbgeId)
                        if (cityFeature) {
                          const cityBounds = L.geoJSON(cityFeature).getBounds()
                          if (cityBounds.isValid()) { mapRef.fitBounds(cityBounds, { padding: [40, 40] }); return }
                        }
                      }
                      const bounds = L.geoJSON(geoJson).getBounds()
                      if (bounds.isValid()) mapRef.fitBounds(bounds, { padding: [20, 20] })
                    } catch { /* */ }
                  }, 100)
                }
              }}>
                <TileLayer
                  attribution='&copy; CARTO'
                  url={mapView === 'pins'
                    ? 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png'
                    : 'https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png'
                  }
                />
                <GeoJSON key={filter + mapView + Object.keys(cityNames).length} data={geoJson} style={styleFeature} onEachFeature={onEachFeature} />
                {mapView === 'pins' && data?.leader_pins?.map(pin => (
                  <CircleMarker
                    key={pin.id}
                    center={[pin.latitude, pin.longitude] as LatLngExpression}
                    radius={4}
                    pathOptions={{ color: '#fff', weight: 1.5, fillColor: '#465fff', fillOpacity: 1 }}
                  >
                    <Tooltip direction="top" offset={[0, -6]}>
                      <strong>{pin.name}</strong>{pin.nickname && <span> ({pin.nickname})</span>}
                    </Tooltip>
                  </CircleMarker>
                ))}
              </MapContainer>
            ) : null}
          </div>
        </Card>

        {/* Legenda */}
        {mapView === 'heat' && (
          <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
            {[['#e4e7ec','Sem presença'],['#bfcefe','Baixo'],['#93aafd','Médio'],['#465fff','Alto'],['#2a31d8','Muito alto']].map(([c,l]) => (
              <span key={l} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ background: c }} />{l}</span>
            ))}
          </div>
        )}

      {/* Ranking */}
      {data && data.cities.length > 0 && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Ranking por cidade</h3>
            {filter === 'campaign' && data.total_vote_goal > 0 && (
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                <button onClick={() => setRankingView('leaders')} className={clsx('px-3 py-1 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5', rankingView === 'leaders' ? 'bg-white text-gray-800 shadow-xs' : 'text-gray-500')}>
                  <Crown className="w-3.5 h-3.5" />Lideranças
                </button>
                <button onClick={() => setRankingView('vote_goal')} className={clsx('px-3 py-1 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5', rankingView === 'vote_goal' ? 'bg-white text-gray-800 shadow-xs' : 'text-gray-500')}>
                  <Target className="w-3.5 h-3.5" />Meta de votos
                </button>
              </div>
            )}
          </div>
          <div className="space-y-2">
            {rankedCities.map((city, i) => {
              const value = getCityValue(city)
              return (
                <button key={city.city_id} onClick={() => openCity(city)} className="w-full flex items-center gap-3 hover:bg-gray-50 rounded-lg p-2 -mx-2 transition-colors group">
                  <span className="w-6 text-right text-xs font-bold text-gray-400">{i + 1}</span>
                  <span className="w-32 text-left text-sm font-medium text-gray-800 group-hover:text-brand-600 truncate">{city.city_name}/{city.state_code}</span>
                  <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${Math.max((value / maxRankingCount) * 100, 8)}%`, backgroundColor: rankingView === 'vote_goal' ? '#f59e0b' : getColor(city.leaders_count) }} />
                  </div>
                  <span className="text-sm font-semibold text-gray-800 w-16 text-right">{rankingView === 'vote_goal' ? value.toLocaleString('pt-BR') : value}</span>
                </button>
              )
            })}
          </div>
        </Card>
      )}
      </>) : null}

      {/* Detalhe cidade */}
      <SlidePanel open={!!selectedCity} onClose={() => setSelectedCity(null)} title={selectedCity ? `${selectedCity.city_name}/${selectedCity.state_code}` : ''} subtitle={`${selectedCity?.leaders_count || 0} liderança${(selectedCity?.leaders_count || 0) !== 1 ? 's' : ''}`}>
        <div className="p-6">
          {cityLoading ? (
            <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />)}</div>
          ) : (
            <div className="space-y-3">
              {cityLeaders.map(l => (
                <div key={l.id} className="bg-white rounded-xl border border-gray-200 p-4">
                  <p className="text-sm font-medium text-gray-900">{l.name}{l.nickname && <span className="text-gray-400 font-normal ml-1">({l.nickname})</span>}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-gray-500">
                    {l.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{formatPhone(l.phone)}</span>}
                    {(l.neighborhood_name || l.neighborhood) && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{l.neighborhood_name || l.neighborhood}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </SlidePanel>
    </div>
  )
}
